import { Table } from 'react-bootstrap';
import { displayName } from '../../../../utilities/conversions';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { RoutineStatus } from '../../../../models';

const {
  scoreSheetListTable,
  scoreSheetListRank,
  scoreSheetListScore,
  scoreSheetListTotal,
  scoreSheetListTie,
  scoreSheetListTeam,
  scoreSheetListOut,
  scoreSheetListTotalEnd,
  teamBreakdownTable,
  aaResultsTable,
  reduceFont,
} = officialscoresheetStyles;

export function TeamSummaryScoreSheetTable({ teams, apparatus }) {
  if (!teams) return null;

  const teamsSorted = teams
    .slice()
    .filter((t) => !t.individuals)
    .sort((a, b) => {
      const rankA = a.rank;
      const rankB = b.rank;
      if (rankA === '' && rankB !== '') {
        return 1;
      } else if (rankA !== '' && rankB === '') {
        return -1;
      } else {
        return rankA - rankB;
      }
    });

  const isReduceFont = apparatus.length > 4 ? reduceFont : null;

  const teamRow = (r, index) => {
    return (
      <tr key={`${index}`}>
        <td className={scoreSheetListRank}>{r.rank}</td>
        <td className={scoreSheetListTie}>{r.isTie ? 'T' : null}</td>
        <td className={scoreSheetListTeam}>{r.displayName}</td>
        {apparatus.map((a, i) => {
          return (
            <td key={`${index}_${a}`} className={scoreSheetListScore}>
              {r?.events?.find((e) => e.apparatus === a)?.eventScore}
            </td>
          );
        })}
        <td className={scoreSheetListTotal}>{r.finalScore}</td>
        <td className={scoreSheetListOut}>{r.rankOut}</td>
      </tr>
    );
  };

  return (
    <Table className={[scoreSheetListTable, isReduceFont].join(' ')}>
      <thead>
        <tr>
          <th className={scoreSheetListRank}>Rank</th>
          <th className={scoreSheetListTie} />
          <th className={scoreSheetListTeam}>Team</th>
          {apparatus.map((a) => {
            return (
              <th key={`header_${a}`} className={scoreSheetListScore}>
                {a}
              </th>
            );
          })}
          <th className={scoreSheetListTotal}>Total</th>
          <th className={scoreSheetListOut}>Out</th>
        </tr>
      </thead>
      <tbody>{teamsSorted.map((t, i) => teamRow(t, i))}</tbody>
    </Table>
  );
}

export function AAScoreSheetTable({ individuals, apparatus }) {
  if (!individuals) return null;

  const individualsSorted = individuals.slice().sort((a, b) => {
    let rankA = a['scores']['AA']['rank'];
    let rankB = b['scores']['AA']['rank'];

    // Treat 0 as bottom of list
    if (rankA === 0 || rankA === '0') rankA = Infinity;
    if (rankB === 0 || rankB === '0') rankB = Infinity;

    if (rankA === Infinity && rankB === Infinity) return 0;

    if (rankA === '' && rankB !== '') {
      return 1;
    } else if (rankA !== '' && rankB === '') {
      return -1;
    } else {
      return rankA - rankB;
    }
  });

  const isReduceFont = apparatus.length > 4 ? reduceFont : null;

  const individualRow = (r, index) => {
    const scores = r?.['scores'];
    const scoreAA = scores?.['AA'];
    const isUnranked = scoreAA?.rank === 0 || scoreAA?.rank === '0';
    const isZeroScore = parseFloat(scoreAA?.score) === 0;

    return (
      <tr key={`${index}`}>
        <td className={scoreSheetListRank}>
          {isUnranked ? '' : scoreAA?.rank}
        </td>
        <td className={scoreSheetListTie}>{scoreAA?.isTie ? 'T' : null}</td>
        <td className={scoreSheetListTeam}>
          <span>{r?.name}</span>
        </td>
        <td className={scoreSheetListTeam}>{r?.team}</td>
        {apparatus.map((a) => {
          return (
            <td key={`${index}_${a}`} className={scoreSheetListScore}>
              {scores[a].score}
            </td>
          );
        })}
        <td className={scoreSheetListTotal}>
          {isZeroScore ? '--' : scoreAA.score}
        </td>
        <td className={scoreSheetListOut}>{scoreAA.rankOut}</td>
      </tr>
    );
  };

  return (
    <Table
      className={[scoreSheetListTable, aaResultsTable, isReduceFont]
        .filter(Boolean)
        .join(' ')}
    >
      <thead>
        <tr>
          <th className={scoreSheetListRank}>Rank</th>
          <th className={scoreSheetListTie} />
          <th className={scoreSheetListTeam}>Name</th>
          <th className={scoreSheetListTeam}>Team</th>
          {apparatus.map((a) => {
            return (
              <th key={`header_${a}`} className={scoreSheetListScore}>
                {a}
              </th>
            );
          })}
          <th className={scoreSheetListTotal}>Total</th>
          <th className={scoreSheetListOut}>Out</th>
        </tr>
      </thead>
      <tbody>
        {individualsSorted.map((athlete, i) => individualRow(athlete, i))}
      </tbody>
    </Table>
  );
}

export function ApparatusScoreSheetTable({
  individuals,
  apparatus,
  teamsByAthleteId,
}) {
  if (!individuals) return null;

  const individualsSorted = individuals.slice().sort((a, b) => {
    const getSortValue = (item) => {
      if (item.status === RoutineStatus.ON_AIR) return -3;
      if (item.status === RoutineStatus.ON_EVAL) return -2;
      if (item.status === RoutineStatus.STANDBY) return -1;

      let rank = item.rank;
      if (rank === '' || rank === 0 || rank === '0') {
        return Infinity;
      }
      return parseFloat(rank);
    };

    const valA = getSortValue(a);
    const valB = getSortValue(b);

    if (valA === Infinity && valB === Infinity) return 0;
    return valA - valB;
  });

  const isReduceFont = apparatus.length > 4 ? reduceFont : null;

  const isActive = (item) =>
    [
      RoutineStatus.ON_AIR,
      RoutineStatus.ON_EVAL,
      RoutineStatus.STANDBY,
    ].includes(item?.status);

  const individualRow = (r, index) => {
    const isUnranked = r?.rank === 0 || r?.rank === '0';
    const isZeroScore = parseFloat(r?.score) === 0;

    const isLastActive =
      isActive(r) &&
      individualsSorted[index + 1] &&
      !isActive(individualsSorted[index + 1]);

    const borderStyle = isLastActive ? { borderBottom: '3px double #ccc' } : {};

    let rankDisplay = isUnranked ? '' : r?.rank;
    let scoreDisplay = isZeroScore ? '--' : r?.score;

    if (r?.status === RoutineStatus.ON_AIR) {
      rankDisplay = (
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
          }}
        ></span>
      );
      scoreDisplay = '--';
    } else if (r?.status === RoutineStatus.ON_EVAL) {
      rankDisplay = (
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#ffc107',
          }}
        ></span>
      );
      scoreDisplay = '--';
    }

    return (
      <tr key={`${index}`}>
        <td className={scoreSheetListRank} style={borderStyle}>
          {rankDisplay}
        </td>
        <td className={scoreSheetListTie} style={borderStyle}>
          {r?.isTie ? 'T' : null}
        </td>
        <td className={scoreSheetListTeam} style={borderStyle}>
          {r?.name}
        </td>
        {/*<td className={scoreSheetListTeam}>{r?.team}</td>*/}
        <td className={scoreSheetListTeam} style={borderStyle}>
          {displayName(
            teamsByAthleteId?.[r.athleteId]?.name,
            teamsByAthleteId?.[r.athleteId]?.altNames
          ) || r?.team}
        </td>
        <td className={scoreSheetListTotal} style={borderStyle}>
          {scoreDisplay}
        </td>
        <td className={scoreSheetListOut} style={borderStyle}>
          {r?.rankOut}
        </td>
      </tr>
    );
  };

  return (
    <Table className={[scoreSheetListTable, isReduceFont].join(' ')}>
      <thead>
        <tr>
          <th className={scoreSheetListRank}>Rank</th>
          <th className={scoreSheetListTie} />
          <th className={scoreSheetListTeam}>Name</th>
          <th className={scoreSheetListTeam}>Team</th>
          <th className={scoreSheetListTotal}>Score</th>
          <th className={scoreSheetListOut}>Out</th>
        </tr>
      </thead>
      <tbody>
        {individualsSorted.map((athlete, i) => individualRow(athlete, i))}
      </tbody>
    </Table>
  );
}

export function TeamBreakdownScoreSheetTable({
  team,
  apparatus,
  teamsByAthleteId,
}) {
  if (!team) return null;

  const athletes = {};

  team.events.forEach((e) => {
    e.lineup.forEach((l) => {
      athletes[l.name] = {
        ...athletes[l.name],
        athleteId: l.athleteId,
        ...{
          [e.apparatus]: {
            isTeamRoutine: l.isTeamRoutine,
            score: l.finalScore,
            exhibition: l.exhibition,
            status: l.status,
          },
        },
      };
    });
  });

  const athleteList = [];
  Object.keys(athletes).forEach((a) => {
    const name = a.trim();
    const lastName = name.split(' ')[name.split(' ').length - 1];
    athleteList.push({
      ...athletes[a],
      name,
      lastName,
    });
  });

  const sortedAthletes = athleteList.slice().sort((a, b) => {
    const nameA = a.lastName.toLowerCase();
    const nameB = b.lastName.toLowerCase();
    if (nameA < nameB) return -1;
    if (nameB > nameA) return 1;
    return 0;
  });

  const isReduceFont = apparatus.length > 4 ? reduceFont : null;

  // Show tricodes only when athletes from multiple teams are present
  const showTriCode = (() => {
    if (!team?.individuals || !teamsByAthleteId) return false;
    const teamIds = new Set();
    for (const a of sortedAthletes) {
      const t = teamsByAthleteId[a.athleteId];
      if (t) teamIds.add(t.id || t.triCode || t.name);
      if (teamIds.size > 1) return true;
    }
    return false;
  })();

  const athleteRow = (r, index) => {
    const teamName = displayName(
      teamsByAthleteId?.[r.athleteId]?.name,
      teamsByAthleteId?.[r.athleteId]?.altNames
    );
    const teamNameStr = showTriCode ? ` (${teamName})` : '';
    const athleteName = r?.name ? `${r?.name}${teamNameStr}` : '';

    return (
      <tr key={`${index}`}>
        <td className={scoreSheetListTeam}>{athleteName.substring(0, 30)}</td>
        {apparatus.map((e) => {
          const isBold = r?.[e]?.isTeamRoutine && !team.individuals;
          let score = r?.[e]?.score;
          const status = r?.[e]?.status;
          let statusIndicator = null;

          if (status === RoutineStatus.ON_AIR) {
            score = '';
            statusIndicator = (
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#28a745',
                  marginRight: '4px',
                }}
              ></span>
            );
          } else if (status === RoutineStatus.ON_EVAL) {
            score = '';
            statusIndicator = (
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#ffc107',
                  marginRight: '4px',
                }}
              ></span>
            );
          }

          return (
            <td key={`${index}_${e}`} className={scoreSheetListScore}>
              {statusIndicator}
              <sub>{r?.[e]?.exhibition ? 'EX ' : ''}</sub>
              <span style={{ fontWeight: isBold ? 'bold' : null }}>
                {score}
              </span>
            </td>
          );
        })}
        <td className={scoreSheetListTotalEnd}>
          {team.aa.find((a) => a.name === r.name)?.scores?.AA?.score}
        </td>
      </tr>
    );
  };

  return (
    <Table
      className={[scoreSheetListTable, teamBreakdownTable, isReduceFont]
        .filter(Boolean)
        .join(' ')}
    >
      <thead>
        <tr>
          <th className={scoreSheetListTeam} style={{ width: '90pt' }}>
            {team.displayName}
          </th>
          {apparatus.map((a) => {
            return (
              <th key={`header_${a}`} className={scoreSheetListScore}>
                {a}
              </th>
            );
          })}
          <th className={scoreSheetListTotalEnd}>AA</th>
        </tr>
      </thead>
      <tbody>{sortedAthletes.map((a, i) => athleteRow(a, i))}</tbody>
      <tfoot>
        <tr>
          {!team.individuals && (
            <>
              <td className={scoreSheetListTeam}>
                Rank #{team.rank}
                {team.isTie ? 'T' : null}
              </td>
              {apparatus.map((a) => {
                return (
                  <td key={`footer_${a}`} className={scoreSheetListScore}>
                    {team.events.find((e) => e.apparatus === a)?.eventScore}
                  </td>
                );
              })}

              <td className={scoreSheetListTotalEnd}>{team.finalScore}</td>
            </>
          )}
        </tr>
      </tfoot>
    </Table>
  );
}

export function LineupVerificationFormTable({ team, apparatus }) {
  const {
    formLineupVerificationTable,
    formLineupVerificationLineup,
    formLineupVerificationUpdate,
    reduceFont,
  } = officialscoresheetStyles;

  const isReduceFont = apparatus.length > 4 ? reduceFont : null;
  const apparatusList = apparatus;

  const placeHolderLineup = [...Array(6).keys()].map((el) => {
    return {
      name: '',
    };
  });

  const lineups = team.events.reduce((acc, e) => {
    if (apparatus.includes(e.apparatus)) {
      acc[e.apparatus] = e.lineup;
    }
    return acc;
  }, {});

  return (
    <Table className={[formLineupVerificationTable, isReduceFont].join(' ')}>
      <thead>
        <tr>
          {apparatusList.map((e) => {
            return (
              <th
                key={`${team.id}_${e}_header`}
                className={formLineupVerificationLineup}
              >
                {e}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        <tr>
          {apparatusList.map((e) => {
            const lineup = lineups?.[e] || placeHolderLineup;

            return (
              <td
                key={`${team.id}_${e}_body`}
                className={formLineupVerificationLineup}
              >
                <ol>
                  {lineup.map((l, i) => {
                    return (
                      <li key={`${l.name}_${i}`}>
                        {l.name}
                        {l.consolidatedRotation != null && (
                          <span style={{ fontSize: '0.75em', opacity: 0.7 }}>
                            {' '}
                            (R{l.consolidatedRotation})
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </td>
            );
          })}
        </tr>
        <tr>
          {apparatusList.map((e) => {
            return (
              <td
                key={`${team.id}_${e}_update`}
                className={formLineupVerificationUpdate}
              >
                <ol>
                  {[...Array(8).keys()].map((l) => {
                    return <li key={l}>________________________</li>;
                  })}
                </ol>
              </td>
            );
          })}
        </tr>
      </tbody>
    </Table>
  );
}
